import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock } from "lucide-react";
import { PendingTaskItem } from "./PendingTaskItem";
import type { Task } from "@/types";

interface QueuedTasksProps {
    tasks: Task[];
    onStart: (id: string) => void;
    onDelete: (id: string) => void;
    onAddTime: (id: string, minutes: number) => void;
    onMoveToBacklog: (id: string) => void;
    onRename: (id: string, newName: string) => void;
    hasActiveTask: boolean;
    onEditComment: (taskId: string, commentId: string, newText: string) => void;
    onDeleteComment: (taskId: string, commentId: string) => void;
    onUpdateTags: (taskId: string, tags: string[]) => void;
    onAddComment: (taskId: string, text: string) => void;
    allTags?: string[]; // All existing tags for autocomplete
}

export function QueuedTasks({
    tasks,
    onStart,
    onDelete,
    onAddTime,
    onMoveToBacklog,
    onRename,
    hasActiveTask,
    onEditComment,
    onDeleteComment,
    onUpdateTags,
    onAddComment,
    allTags = [],
}: QueuedTasksProps) {
    const pendingTasks = tasks.filter((t) => t.status === "pending");

    if (pendingTasks.length === 0) {
        return null;
    }

    return (
        <Card className="border-0 shadow-md">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <div className="p-1.5 bg-blue-500/10 rounded-lg">
                        <Clock className="w-4 h-4 text-blue-500" />
                    </div>
                    Up Next
                    <Badge variant="secondary" className="ml-auto">
                        {pendingTasks.length} task
                        {pendingTasks.length > 1 ? "s" : ""}
                    </Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {pendingTasks.map((task) => (
                    <PendingTaskItem
                        key={task.id}
                        task={task}
                        onStart={() => onStart(task.id)}
                        onDelete={() => onDelete(task.id)}
                        onAddTime={(mins) => onAddTime(task.id, mins)}
                        onMoveToBacklog={() => onMoveToBacklog(task.id)}
                        onRename={(newName) => onRename(task.id, newName)}
                        canStart={!hasActiveTask}
                        onEditComment={(commentId, text) =>
                            onEditComment(task.id, commentId, text)
                        }
                        onDeleteComment={(commentId) =>
                            onDeleteComment(task.id, commentId)
                        }
                        onUpdateTags={(tags) => onUpdateTags(task.id, tags)}
                        onAddComment={(text) => onAddComment(task.id, text)}
                        allTags={allTags}
                    />
                ))}
            </CardContent>
        </Card>
    );
}
